import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { SuperAdminLayout } from './SuperAdminLayout';
import {
  CreditCard, ArrowUpRight, Calendar, Clock, Search,
  ChevronUp, ChevronDown, Edit, X, Check
} from 'lucide-react';

const PLAN_COLORS = {
  start: { bg: 'bg-blue-500/15', text: 'text-blue-400', border: 'border-blue-500/20' },
  pro: { bg: 'bg-[#fedd00]/15', text: 'text-[#fedd00]', border: 'border-[#fedd00]/20' },
  super: { bg: 'bg-[#00d946]/15', text: 'text-[#00d946]', border: 'border-[#00d946]/20' },
};

// Les clés sont des valeurs backend (non traduites) ; le libellé affiché passe par i18n.
const STATUS_MAP = {
  active: { label: 'Actif', cls: 'bg-emerald-500/15 text-emerald-400' },
  suspended: { label: 'Suspendu', cls: 'bg-red-500/15 text-red-400' },
  expired: { label: 'Expiré', cls: 'bg-gray-500/15 text-gray-400' },
  overdue: { label: 'Retard', cls: 'bg-amber-500/15 text-amber-400' },
  cancelled: { label: 'Annulé', cls: 'bg-gray-500/15 text-gray-400' },
};

const STATUS_LABEL_KEYS = {
  active: 'saSubscriptions.status_active',
  suspended: 'saSubscriptions.status_suspended',
  expired: 'saSubscriptions.status_expired',
  overdue: 'saSubscriptions.status_overdue',
  cancelled: 'saSubscriptions.status_cancelled',
};

const SuperAdminSubscriptions = () => {
  const { api } = useAuth();
  const { t, formatCurrency, formatDate } = useLanguage();
  const [subs, setSubs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [editingSub, setEditingSub] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchSubs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (planFilter) params.append('plan', planFilter);
      const res = await api.get(`/super-admin/subscriptions?${params}`);
      setSubs(res.data.subscriptions);
      setTotal(res.data.total);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [api, statusFilter, planFilter]);

  useEffect(() => { fetchSubs(); }, [fetchSubs]);

  const handleUpdateSub = async () => {
    if (!editingSub) return;
    try {
      await api.put(`/super-admin/subscriptions/${editingSub.id}`, editForm);
      setEditingSub(null);
      fetchSubs();
    } catch (err) { alert(err.response?.data?.detail || t('saSubscriptions.error')); }
  };

  const handleExtend = async (subId) => {
    const days = prompt(t('saSubscriptions.prompt_extend_days'), '30');
    if (!days) return;
    try {
      await api.post(`/super-admin/subscriptions/${subId}/extend`, { days: parseInt(days) });
      fetchSubs();
    } catch (err) { alert(err.response?.data?.detail || t('saSubscriptions.error')); }
  };

  return (
    <SuperAdminLayout>
      <div className="space-y-4" data-testid="super-admin-subscriptions">
        <div>
          <h1 className="text-xl font-bold text-white">{t('saSubscriptions.title')}</h1>
          <p className="text-sm text-gray-500">{t('saSubscriptions.count', { count: total })}</p>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="bg-[#111827] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-gray-300 focus:outline-none" data-testid="filter-sub-status">
            <option value="">{t('saSubscriptions.filter_all_statuses')}</option>
            <option value="active">{t('saSubscriptions.status_active')}</option>
            <option value="suspended">{t('saSubscriptions.status_suspended')}</option>
            <option value="expired">{t('saSubscriptions.status_expired')}</option>
            <option value="overdue">{t('saSubscriptions.status_overdue')}</option>
          </select>
          <select value={planFilter} onChange={e => setPlanFilter(e.target.value)} className="bg-[#111827] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-gray-300 focus:outline-none" data-testid="filter-sub-plan">
            <option value="">{t('saSubscriptions.filter_all_plans')}</option>
            <option value="start">Start</option>
            <option value="pro">Pro</option>
            <option value="super">Super</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-[#111827] border border-white/5 rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <div className="w-6 h-6 border-2 border-[#009b3a] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : subs.length === 0 ? (
            <div className="p-12 text-center">
              <CreditCard className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500">{t('saSubscriptions.empty')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/5 text-gray-500 text-xs">
                    <th className="text-left px-4 py-3 font-medium">{t('saSubscriptions.col_company')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('saSubscriptions.col_plan')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('saSubscriptions.col_price')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('saSubscriptions.col_status')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('saSubscriptions.col_next_billing')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('saSubscriptions.col_discount')}</th>
                    <th className="text-right px-4 py-3 font-medium">{t('saSubscriptions.col_actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {subs.map(s => {
                    const planColor = PLAN_COLORS[s.plan] || PLAN_COLORS.start;
                    const status = STATUS_MAP[s.status] || STATUS_MAP.active;
                    const statusLabel = STATUS_LABEL_KEYS[s.status] ? t(STATUS_LABEL_KEYS[s.status]) : status.label;
                    return (
                      <tr key={s.id} className="hover:bg-white/[0.02]" data-testid={`sub-row-${s.id}`}>
                        <td className="px-4 py-3 text-white">{s.company_name}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-bold uppercase ${planColor.bg} ${planColor.text} border ${planColor.border}`}>
                            {s.plan_name}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-300">{formatCurrency(s.monthly_price)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${status.cls}`}>
                            {statusLabel}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-xs">
                          {s.next_billing_date ? formatDate(s.next_billing_date) : '-'}
                        </td>
                        <td className="px-4 py-3 text-gray-400">{s.discount_pct || 0}%</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => { setEditingSub(s); setEditForm({ plan: s.plan, discount_pct: s.discount_pct || 0, status: s.status }); }}
                              className="p-1.5 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white" title={t('saSubscriptions.action_edit')}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleExtend(s.id)} className="p-1.5 rounded-lg hover:bg-[#009b3a]/10 text-gray-400 hover:text-[#009b3a]" title={t('saSubscriptions.action_extend')}>
                              <Calendar className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editingSub && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111827] border border-white/10 rounded-2xl w-full max-w-md" data-testid="edit-sub-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
              <h2 className="text-base font-bold text-white">{t('saSubscriptions.modal_title')}</h2>
              <button onClick={() => setEditingSub(null)} className="p-1 rounded-lg hover:bg-white/5">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-400">{editingSub.company_name}</p>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('saSubscriptions.col_plan')}</label>
                <select value={editForm.plan} onChange={e => setEditForm(p => ({...p, plan: e.target.value}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white" data-testid="edit-sub-plan">
                  <option value="start">{t('saSubscriptions.plan_option_start', { price: formatCurrency(29.99) })}</option>
                  <option value="pro">{t('saSubscriptions.plan_option_pro', { price: formatCurrency(59.99) })}</option>
                  <option value="super">{t('saSubscriptions.plan_option_super', { price: formatCurrency(99.99) })}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('saSubscriptions.col_status')}</label>
                <select value={editForm.status} onChange={e => setEditForm(p => ({...p, status: e.target.value}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white">
                  <option value="active">{t('saSubscriptions.status_active')}</option>
                  <option value="suspended">{t('saSubscriptions.status_suspended')}</option>
                  <option value="expired">{t('saSubscriptions.status_expired')}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('saSubscriptions.label_discount')}</label>
                <input type="number" min="0" max="100" value={editForm.discount_pct} onChange={e => setEditForm(p => ({...p, discount_pct: parseInt(e.target.value) || 0}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setEditingSub(null)} className="px-4 py-2.5 text-sm text-gray-400">{t('saSubscriptions.cancel')}</button>
                <button onClick={handleUpdateSub} data-testid="save-sub-btn" className="px-5 py-2.5 bg-[#009b3a] text-white rounded-lg text-sm font-medium hover:bg-[#00b344]">{t('saSubscriptions.save')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
};

export default SuperAdminSubscriptions;
